import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Skeleton, Stack } from "@mui/material";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { CaregiverInvolvementFormValues } from "./interface";
import useGetCaregiverInvolvement from "hooks/api/client/useGetCaregiverInvolvement";
import useSaveCaregiverInvolvement from "hooks/api/client/useSaveCaregiverInvolvement";

const EMPTY_VALUES: CaregiverInvolvementFormValues = {
  involvement: "",
  barriersToTreatmentAdherence: "",
  barriersToParticipation: "",
  summaryCaregiverParticipation: "",
  locationTrainingSessions: "",
};

export const CaregiverInvolvement: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { caregiverInvolvement, refetchCaregiverInvolvement, isFetched: isCaregiverInvolvementFetched } =
    useGetCaregiverInvolvement(clientIdNum);

  const { saveCaregiverInvolvementMutation, isSaveCaregiverInvolvementLoading } =
    useSaveCaregiverInvolvement({
      onSuccess: () => {
        refetchCaregiverInvolvement();
        setEditMode(false);
      },
    });

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<CaregiverInvolvementFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<CaregiverInvolvementFormValues>(EMPTY_VALUES);

  useEffect(() => {
    if (caregiverInvolvement) {
      const values: CaregiverInvolvementFormValues = {
        involvement: caregiverInvolvement.involvement || "",
        barriersToTreatmentAdherence: caregiverInvolvement.barriers_to_treatment_adherence || "",
        barriersToParticipation: caregiverInvolvement.barriers_to_participation || "",
        summaryCaregiverParticipation: caregiverInvolvement.summary_caregiver_participation || "",
        locationTrainingSessions: caregiverInvolvement.location_training_sessions || "",
      };
      setFormValues(values);
      setInitialValues(values);
    }
  }, [caregiverInvolvement]);

  const handleChange =
    (field: keyof CaregiverInvolvementFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleSave = () => {
    if (!clientIdNum) return;
    const payload = {
      involvement: formValues.involvement || null,
      barriers_to_treatment_adherence: formValues.barriersToTreatmentAdherence || null,
      barriers_to_participation: formValues.barriersToParticipation || null,
      summary_caregiver_participation: formValues.summaryCaregiverParticipation || null,
      location_training_sessions: formValues.locationTrainingSessions || null,
    };
    saveCaregiverInvolvementMutation({
      clientId: clientIdNum,
      recordId: caregiverInvolvement?.an_internal_id,
      payload,
    });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled =
    JSON.stringify(formValues) === JSON.stringify(initialValues) ||
    isSaveCaregiverInvolvementLoading;

  if (!isCaregiverInvolvementFetched) {
    return (
      <SectionWrapper
        title="Caregiver Involvement"
        description="Caregiver participation patterns and any challenges they're facing"
        hideButtons
      >
        <Stack gap={2}>
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="rounded" height={56} />
        </Stack>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Caregiver Involvement"
      description="Caregiver participation patterns and any challenges they're facing"
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}
    </SectionWrapper>
  );
};

export default CaregiverInvolvement;
